const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
class NappiProduct extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector("script[data-nappi-product]");
    if (!data) return;
    this.product = JSON.parse(data.textContent);
    this.form = this.querySelector("[data-nappi-form]");
    this.addEventListener("change", (event) => {
      const input = event.target;
      if (input instanceof HTMLSelectElement && input.closest("[data-pair]")) {
        this.#onPairChange(input);
        return;
      }
      if (input instanceof HTMLInputElement && input.dataset.optionPosition) {
        this.#onOptionChange(input);
        if (this.bundle && Number(input.dataset.optionPosition) === this.sizePosition) this.#setAllSizes(input.value);
        if (this.bundle && Number(input.dataset.optionPosition) === this.colorPosition) this.#setActiveColor(input.value);
      }
    });
    this.#syncAvailability();
    this.#bindGender();
    this.#bindBundle();
    this.#bindTiers();
    this.#detectShippingCountry();
    this.onLinesUpdate = (event) => this.#onAdd(event);
    document.addEventListener("shopify:cart:lines-update", this.onLinesUpdate);
    this.querySelectorAll("[data-pack-index]").forEach((link) => {
      link.addEventListener("click", (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        const nav = link.closest(".nappi-product__packs");
        if (!nav || reducedMotion()) return;
        event.preventDefault();
        nav.style.setProperty("--n-index", link.dataset.packIndex);
        setTimeout(() => {
          window.location.href = link.href;
        }, 250);
      });
    });
  }
  disconnectedCallback() {
    document.removeEventListener("shopify:cart:lines-update", this.onLinesUpdate);
  }
  #onAdd(event) {
    if (event?.action !== "add" || event.context !== "product") return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const button = this.querySelector("[data-nappi-add]");
    const label = button?.querySelector("[data-nappi-add-label]");
    if (!button || !label || !button.dataset.labelAdding) return;
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    label.textContent = button.dataset.labelAdding;
    const done = () => {
      button.removeAttribute("aria-busy");
      const available = this.bundle ? this.bundleAvailable !== false : this.variant ? this.variant.available : true;
      button.disabled = !available;
      label.textContent = available ? button.dataset.labelAdd : button.dataset.labelSoldOut;
    };
    Promise.resolve(event.promise).catch(() => {
    }).finally(() => requestAnimationFrame(done));
  }
  // Paliers de quantité : le palier choisi pilote la quantité envoyée au panier.
  // Les prix affichés (total, prix à la paire, prix barré) se recalculent depuis
  // le prix de la variante, comme en Liquid, pour rester justes après changement
  // de taille ou de couleur.
  // Pays du visiteur d'après son adresse IP (point d'accès de géolocalisation de
  // Shopify) ; le rendu serveur (localization.country) sert de valeur de départ.
  // Seulement s'il fait partie des pays livrés (data-ship-countries, 26/09/2026) :
  // la barre annonçait « Fast BR Shipping » à un visiteur du Brésil, où la
  // boutique ne livre pas.
  async #detectShippingCountry() {
    const bar = this.querySelector("[data-ship-bar]");
    if (!bar) return;
    const key = "nappi:detected-country";
    let code = null;
    try {
      code = sessionStorage.getItem(key);
    } catch (e) {
    }
    if (!code) {
      try {
        const res = await fetch("/browsing_context_suggestions.json", { headers: { Accept: "application/json" } });
        const data = await res.json();
        code = data?.detected_values?.country?.handle || null;
        if (code) {
          try {
            sessionStorage.setItem(key, code);
          } catch (e) {
          }
        }
      } catch (e) {
        return;
      }
    }
    if (!code || !/^[A-Z]{2}$/.test(code)) return;
    const served = (bar.dataset.shipCountries || "").split(",").filter(Boolean);
    if (served.length && !served.includes(code)) return;
    const flag = String.fromCodePoint(...[...code].map((c) => 127462 + c.charCodeAt(0) - 65));
    const flagEl = bar.querySelector("[data-ship-flag]");
    const textEl = bar.querySelector("[data-ship-country]");
    if (flagEl) flagEl.textContent = flag;
    if (textEl && bar.dataset.shipTemplate) {
      const parts = bar.dataset.shipTemplate.split("[country]");
      textEl.textContent = "";
      textEl.append(parts[0] || "");
      if (parts.length > 1) {
        const strong = document.createElement("strong");
        strong.textContent = code;
        textEl.append(strong, parts[1] || "");
      }
    }
  }
  /* Sélecteur Femme / Homme (01/11/2026) : les notes de taille (« US Women
     8-10½, Men 6-9 ») ne gardent que la partie du choix, préfixe du système
     (« US ») compris ; l'onglet du questionnaire suit. */
  #bindGender() {
    this.gender = "women";
    const buttons = [...this.querySelectorAll("[data-gender]")];
    if (!buttons.length) return;
    this.genderOn = true;
    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        this.gender = button.dataset.gender;
        buttons.forEach((b) => b.setAttribute("aria-pressed", String(b === button)));
        this.#renderSizeNotes();
        if (this.bundle) this.#renderBundle();
        const scope = this.closest(".shopify-section") || document;
        scope.querySelector(`nappi-fit-quiz [data-fq-tab="${this.gender}"]`)?.click();
      });
    });
  }
  #genderNote(text) {
    if (!text || !this.genderOn) return text || "";
    const parts = String(text).split(",").map((p) => p.trim()).filter(Boolean);
    const test = this.gender === "men" ? /\b(men|man|hommes?)\b/i : /\b(women|woman|femmes?)\b/i;
    const part = parts.find((p) => test.test(p));
    if (!part) return "";
    const prefix = parts[0].match(/^(US|EU|UK|CA|AU)\s/);
    return prefix && !part.startsWith(prefix[0]) ? `${prefix[0]}${part}` : part;
  }
  #renderSizeNotes() {
    const none = this.querySelector("[data-gender-none]")?.dataset.genderNone || "";
    this.querySelectorAll("[data-size-note]").forEach((el) => {
      const checked = this.querySelector(`input[data-option-position="${el.dataset.sizeNote}"]:checked`);
      const note = checked?.dataset.note || "";
      el.textContent = note ? this.#genderNote(note) || none : "";
    });
  }
  #bindTiers() {
    this.tiers = [...this.querySelectorAll("[data-tier]")];
    if (!this.tiers.length) return;
    this.quantityInput = this.querySelector("[data-nappi-quantity]");
    this.tiers.forEach((tier) => {
      const input = tier.querySelector("[data-tier-input]");
      if (input) input.addEventListener("change", () => this.#applyTier());
      if (this.bundle && input) {
        tier.addEventListener("click", (event) => {
          if (event.target === input || !input.checked) return;
          event.preventDefault();
          input.checked = false;
          this.#applyTier();
        });
      }
    });
    this.#resetTier();
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) this.#resetTier();
    });
  }
  #resetTier() {
    const wanted = new URLSearchParams(window.location.search).get("pack");
    const inputs = (this.tiers || []).map((tier) => tier.querySelector("[data-tier-input]")).filter(Boolean);
    const target = wanted ? inputs.find((input) => input.value === wanted) : null;
    inputs.forEach((input) => {
      input.checked = target ? input === target : input.defaultChecked;
    });
    this.#applyTier();
  }
  #applyTier() {
    const checked = this.querySelector("[data-tier-input]:checked");
    const tier = checked ? checked.closest("[data-tier]") : this.bundle ? null : this.tiers[0];
    if (this.quantityInput && (tier || this.bundle)) this.quantityInput.value = tier?.dataset.qty || "1";
    if (this.bundle) this.#setPairCount(tier ? Number(tier.dataset.qty) || 1 : 0);
    this.#renderSeg();
  }
  /*
   * Bundle façon Hike (01/11/2026, demande d'Erevan) : une ligne couleur +
   * taille par paire, autant de lignes que le lot coché compte de paires.
   * « Add Another Pair » passe au lot suivant ; la croix d'une ligne la retire
   * et revient au lot précédent, en gardant les premières paires restantes —
   * seuls les lots existent (3 paires coûteraient plus que 4).
   * La paire touchée en dernier devient la variante courante (galerie, prix,
   * URL), par les pastilles et tuiles masquées de la fiche.
   */
  #bindBundle() {
    this.bundle = this.querySelector("[data-bundle]");
    if (!this.bundle) return;
    try {
      this.bundleData = JSON.parse(this.bundle.querySelector("[data-bundle-data]")?.textContent || "{}");
    } catch (e) {
      this.bundleData = {};
    }
    this.colorPosition = Number(this.bundleData.colorPosition) || 0;
    this.sizePosition = Number(this.bundleData.sizePosition) || 0;
    this.pairList = this.bundle.querySelector("[data-pair-list]");
    this.pairTemplate = this.bundle.querySelector("[data-pair-template]");
    this.bundleItems = this.querySelector("[data-nappi-bundle-items]");
    this.bundle.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest("[data-pair-add]")) {
        this.#stepTier(1);
        return;
      }
      const remove = event.target.closest("[data-pair-remove]");
      if (remove) this.#removePair(remove.closest("[data-pair]"));
    });
  }
  #pairs() {
    return this.pairList ? [...this.pairList.querySelectorAll("[data-pair]")] : [];
  }
  #pairVariant(row) {
    const color = row.querySelector("[data-pair-color]")?.value;
    const size = row.querySelector("[data-pair-size]")?.value;
    return this.product.variants.find(
      (v) => (!this.colorPosition || v.options[this.colorPosition - 1] === color) && (!this.sizePosition || v.options[this.sizePosition - 1] === size)
    );
  }
  /* Lots triés par nombre de paires, pour trouver le suivant ou le précédent. */
  #sortedTiers() {
    return (this.tiers || []).map((tier) => ({ tier, qty: Number(tier.dataset.qty) || 1 })).sort((a, b) => a.qty - b.qty);
  }
  #stepTier(direction, count = this.#pairs().length) {
    const list = this.#sortedTiers();
    const target = direction > 0 ? list.find((t) => t.qty > count) : list.reverse().find((t) => t.qty < count);
    const input = target?.tier.querySelector("[data-tier-input]");
    if (!input) return false;
    input.checked = true;
    this.#applyTier();
    return true;
  }
  #removePair(row) {
    if (!row) return;
    const count = this.#pairs().length;
    if (!this.#sortedTiers().some((t) => t.qty < count)) return;
    row.remove();
    this.#stepTier(-1, count);
    this.bundle.querySelector("[data-pair-add]")?.focus();
  }
  #setPairCount(count) {
    if (!this.pairList || !this.pairTemplate) return;
    const pairs = this.#pairs();
    while (pairs.length > count) pairs.pop().remove();
    while (pairs.length < count) pairs.push(this.#newPair(pairs));
    const box = this.bundle.querySelector("[data-bundle-pairs]");
    if (box) box.hidden = count === 0;
    if (!count) this.activePair = null;
    this.#renderBundle();
  }
  #currentVariant() {
    return this.variant || this.#findVariant(this.selectedValues) || this.product.variants[0];
  }
  /* Nouvelle paire : la couleur suivante pas encore prise (des coloris variés,
     comme la référence), la taille de la première paire. La première paire
     reprend la couleur et la taille déjà choisies sur la fiche. */
  #newPair(existing) {
    const holder = document.createElement("div");
    holder.innerHTML = this.pairTemplate.innerHTML.split("__N__").join(String(existing.length + 1)).trim();
    const row = holder.firstElementChild;
    const colorSelect = row.querySelector("[data-pair-color]");
    if (!existing.length) {
      const options = this.#currentVariant()?.options || [];
      const sizeSelect2 = row.querySelector("[data-pair-size]");
      if (colorSelect && this.colorPosition) colorSelect.value = options[this.colorPosition - 1];
      if (sizeSelect2 && this.sizePosition) sizeSelect2.value = options[this.sizePosition - 1];
      this.pairList.appendChild(row);
      return row;
    }
    if (colorSelect) {
      const values = [...colorSelect.options].map((o) => o.value);
      const used = existing.map((r) => r.querySelector("[data-pair-color]")?.value);
      const start = values.indexOf(used[used.length - 1]);
      const ordered = values.map((_, i) => values[(start + 1 + i + values.length) % values.length]);
      colorSelect.value = ordered.find((v) => !used.includes(v)) || ordered[0];
    }
    const sizeSelect = row.querySelector("[data-pair-size]");
    const firstSize = existing[0]?.querySelector("[data-pair-size]")?.value;
    if (sizeSelect && firstSize) sizeSelect.value = firstSize;
    this.pairList.appendChild(row);
    return row;
  }
  #setActiveColor(color) {
    const pairs = this.#pairs();
    const row = this.activePair && this.activePair.isConnected ? this.activePair : pairs[0];
    const select = row?.querySelector("[data-pair-color]");
    if (!select || ![...select.options].some((o) => o.value === color)) return;
    select.value = color;
    this.#renderBundle();
  }
  #onPairChange(select) {
    const row = select.closest("[data-pair]");
    this.activePair = row;
    if (select.matches("[data-pair-size]")) {
      row.dataset.sizeSet = "1";
      const pairs = this.#pairs();
      if (row === pairs[0]) {
        pairs.slice(1).forEach((other) => {
          const s = other.querySelector("[data-pair-size]");
          if (s && !other.dataset.sizeSet) s.value = select.value;
        });
      }
    }
    this.#showPair(row);
    this.#renderBundle();
  }
  #setAllSizes(size) {
    this.#pairs().forEach((row) => {
      const s = row.querySelector("[data-pair-size]");
      if (s && [...s.options].some((o) => o.value === size)) s.value = size;
    });
    this.#renderBundle();
  }
  /* Coche les pastilles et tuiles masquées sur la paire, sans événement, puis
     applique la variante comme un choix du visiteur. */
  #showPair(row) {
    const variant = this.#pairVariant(row);
    if (!variant) return;
    let first = null;
    variant.options.forEach((value, index) => {
      const radio = this.querySelector(`input[data-option-position="${index + 1}"][value="${CSS.escape(value)}"]`);
      if (radio) {
        radio.checked = true;
        first = first || radio;
      }
    });
    if (first) this.#onOptionChange(first);
  }
  #renderBundle() {
    if (!this.pairList) return;
    const data = this.bundleData || {};
    const pairs = this.#pairs();
    const count = pairs.length;
    const items = /* @__PURE__ */ new Map();
    let allAvailable = true;
    const canRemove = this.#sortedTiers().some((t) => t.qty < count);
    pairs.forEach((row, i) => {
      const n = String(i + 1);
      const title = row.querySelector("[data-pair-title]");
      if (title && data.pairLabel) title.textContent = data.pairLabel.replace("[n]", n);
      const remove = row.querySelector("[data-pair-remove]");
      if (remove) {
        remove.hidden = !canRemove;
        if (data.removeLabel) remove.setAttribute("aria-label", data.removeLabel.replace("[n]", n));
      }
      const variant = this.#pairVariant(row);
      const available = Boolean(variant && variant.available);
      if (!available) allAvailable = false;
      const stock = row.querySelector("[data-pair-stock]");
      if (stock) {
        stock.classList.toggle("is-out", !available);
        const text = stock.querySelector("[data-pair-stock-text]");
        if (text) text.textContent = available ? data.inStock || "" : data.soldOut || "";
      }
      const color = row.querySelector("[data-pair-color]")?.value;
      const thumb = row.querySelector("[data-pair-thumb]");
      const src = data.thumbs?.[color];
      if (thumb && src && thumb.getAttribute("src") !== src) thumb.src = src;
      const size = row.querySelector("[data-pair-size]")?.value;
      const note = row.querySelector("[data-pair-note]");
      if (note) {
        const detail = this.#genderNote(data.notes?.[size]);
        note.textContent = detail ? `${size}: ${detail}` : "";
        note.hidden = !detail;
      }
      if (variant) items.set(String(variant.id), (items.get(String(variant.id)) || 0) + 1);
    });
    if (!count) allAvailable = Boolean(this.#currentVariant()?.available);
    if (this.bundleItems) {
      this.bundleItems.value = count ? JSON.stringify([...items].map(([variantId, quantity]) => ({ variantId, quantity }))) : "";
    }
    this.bundleAvailable = allAvailable;
    const more = this.bundle.querySelector("[data-pair-add]");
    if (more) {
      const next = this.#sortedTiers().find((t) => t.qty > count);
      more.hidden = !next;
      const label = more.querySelector("[data-pair-add-label]");
      if (next && label) {
        const diff = next.qty - count;
        label.textContent = diff === 1 ? data.addOne || "" : (data.addMany || "").replace("[count]", String(diff));
      }
      const sub = more.querySelector("[data-pair-add-sub]");
      if (sub) {
        const unit = this.variant?.price ?? this.product.variants[0]?.price ?? 0;
        const full = next ? unit * next.qty : 0;
        const pct = full > 0 ? Math.round((full - this.#tierTotal(unit, next.tier)) * 100 / full) : 0;
        sub.textContent = data.addSub && pct > 0 ? data.addSub.replace("[pct]", String(pct)) : "";
        sub.hidden = !sub.textContent;
      }
    }
    const checked = this.querySelector("[data-tier-input]:checked");
    const tier = checked ? checked.closest("[data-tier]") : null;
    const price = this.variant?.price ?? this.product.variants[0]?.price ?? 0;
    const total = tier ? this.#tierTotal(price, tier) : price;
    const compare = price * (Number(tier?.dataset.qty) || 1);
    const totalEl = this.querySelector("[data-bundle-total]");
    if (totalEl) totalEl.textContent = this.#formatMoney(total);
    const compareEl = this.querySelector("[data-bundle-compare]");
    if (compareEl) {
      compareEl.textContent = this.#formatMoney(compare);
      compareEl.hidden = !(compare > total);
    }
    const button = this.querySelector("[data-nappi-add]");
    if (button && !button.hasAttribute("aria-busy")) {
      button.disabled = !allAvailable;
      const label = button.querySelector("[data-nappi-add-label]");
      if (label) label.textContent = allAvailable ? button.dataset.labelAdd : button.dataset.labelSoldOut;
    }
  }
  /* Présentation « onglets » (29/09/2026) : prix du lot coché, prix barré
     (la ligne de prix est sous le titre depuis le 30/09, hors du fieldset),
     (toutes les paires au prix unitaire) et mention du palier. Même calcul
     qu'en Liquid ; le prix unitaire est celui de la variante courante. */
  #renderSeg() {
    const seg = this.querySelector("[data-seg]");
    if (!seg) return;
    const price = Number(seg.dataset.price) || 0;
    const checked = seg.querySelector("[data-tier-input]:checked");
    const tier = checked ? checked.closest("[data-tier]") : seg.querySelector("[data-tier]");
    if (!tier) return;
    const qty = Number(tier.dataset.qty) || 1;
    const total = this.#tierTotal(price, tier);
    const compare = price * qty;
    const totalEl = this.querySelector("[data-seg-total]");
    if (totalEl) totalEl.textContent = this.#formatMoney(total);
    const compareEl = this.querySelector("[data-seg-compare]");
    if (compareEl) {
      compareEl.textContent = this.#formatMoney(compare);
      compareEl.closest("s").hidden = !(compare > total);
    }
    const banner = this.querySelector(".nappi-product__buybox-banner");
    if (banner) {
      banner.textContent = tier.dataset.segBanner || "";
      banner.hidden = !banner.textContent;
    }
    const ship = this.querySelector("[data-ship-banner]");
    const threshold = ship ? this.#freeShippingThreshold(ship) : null;
    if (ship && threshold !== null) {
      const free = total >= threshold;
      ship.toggleAttribute("data-free", free);
      const shipText = ship.querySelector("[data-ship-text]");
      if (shipText) shipText.textContent = free ? ship.dataset.freeText : ship.dataset.noneText;
    }
    const note = this.querySelector(".nappi-product__seg-price [data-seg-note]");
    if (note) {
      note.textContent = tier.dataset.segNote || "";
      note.hidden = !note.textContent;
    }
  }
  /* Seuil de livraison offerte dans la devise de la page, en centimes : même
     formule que window.NappiMoney.seuil (assets/nappi-money.js), pas encore
     chargé quand la fiche s'initialise — seuil boutique tel quel dans la
     devise de la boutique, sinon × taux du marché, arrondi. null si inconnu. */
  #freeShippingThreshold(el) {
    const base = Number(el.dataset.thresholdShopCents);
    if (!Number.isFinite(base) || base <= 0) return null;
    const currency = window.Shopify?.currency;
    if (!currency?.active || currency.active === el.dataset.shopCurrency) return base;
    const rate = Number(currency.rate);
    return Number.isFinite(rate) && rate > 0 ? Math.round(base * rate) : null;
  }
  /* Prix d'un lot, comme en Liquid (21/10/2026) : paires payées × prix de la
     paire, moins la remise supplémentaire du palier (data-extra, en %, le
     lot 2+2 à 49,99 $), arrondi au centime. */
  #tierTotal(price, tier) {
    const qty = Number(tier.dataset.qty) || 1;
    const charged = Math.min(Number(tier.dataset.charged) || 1, qty);
    const extra = Math.min(Math.max(Number(tier.dataset.extra) || 0, 0), 99);
    return Math.round(price * charged * (100 - extra) / 100);
  }
  #refreshTierPrices(price) {
    const seg = this.querySelector("[data-seg]");
    if (seg) {
      seg.dataset.price = String(price);
      this.#renderSeg();
    }
    const best = (this.tiers || []).reduce((min, tier) => {
      const qty = Number(tier.dataset.qty) || 1;
      return Math.min(min, Math.round(this.#tierTotal(price, tier) / qty));
    }, price);
    this.querySelectorAll("[data-title-offer-price]").forEach((el) => {
      el.textContent = this.#formatMoney(best);
    });
    (this.tiers || []).forEach((tier) => {
      const qty = Number(tier.dataset.qty) || 1;
      const total = this.#tierTotal(price, tier);
      const set = (sel, value) => {
        const el = tier.querySelector(sel);
        if (el) el.textContent = this.#formatMoney(value);
      };
      set("[data-tier-total]", Math.round(total / qty));
      set("[data-tier-compare]", price);
      set("[data-tier-sum]", total);
      set("[data-tier-sum-compare]", price * qty);
      const save = tier.querySelector("[data-tier-save]");
      if (save) {
        const mot = save.dataset.saveWord || "";
        save.textContent = `${mot} ${this.#formatMoney(price * qty - total, { trimZeros: true })}`.trim();
      }
      const pct = tier.querySelector("[data-tier-pct]");
      if (pct) {
        const value = price > 0 ? Math.round((price * qty - total) * 100 / (price * qty)) : 0;
        pct.textContent = (pct.dataset.pctLabel || "").replace("[pct]", String(value));
        pct.hidden = value < 1;
      }
    });
  }
  get selectedValues() {
    return this.product.options.map((_, index) => {
      const checked = this.querySelector(`input[data-option-position="${index + 1}"]:checked`);
      return checked ? checked.value : null;
    });
  }
  #findVariant(values) {
    return this.product.variants.find((variant) => variant.options.every((value, index) => value === values[index]));
  }
  #onOptionChange(input) {
    const position = Number(input.dataset.optionPosition);
    let values = this.selectedValues;
    let variant = this.#findVariant(values);
    if (!variant) {
      const candidates = this.product.variants.filter((v) => v.options[position - 1] === input.value);
      variant = candidates.find((v) => v.available) || candidates[0];
      if (variant) {
        values = variant.options;
        values.forEach((value, index) => {
          const radio = this.querySelector(
            `input[data-option-position="${index + 1}"][value="${CSS.escape(value)}"]`
          );
          if (radio) radio.checked = true;
        });
      }
    }
    if (!variant) return;
    this.#applyVariant(variant);
  }
  #applyVariant(variant) {
    this.variant = variant;
    const idInput = this.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
    variant.options.forEach((value, index) => {
      this.querySelectorAll(`[data-option-value-label="${index + 1}"]`).forEach((el) => {
        el.textContent = value;
      });
    });
    this.querySelectorAll("[data-swatch-group]").forEach((group) => {
      const holder = group.querySelector("[data-group-value]");
      if (!holder) return;
      const inGroup = group.querySelector("input:checked");
      holder.textContent = inGroup ? inGroup.value : "";
    });
    this.#renderSizeNotes();
    this.querySelectorAll("[data-nappi-price]").forEach((el) => {
      el.textContent = this.#formatMoney(variant.price);
    });
    this.querySelectorAll("[data-nappi-compare-price]").forEach((el) => {
      const show = variant.compare_at_price && variant.compare_at_price > variant.price;
      el.hidden = !show;
      if (show) el.textContent = this.#formatMoney(variant.compare_at_price);
    });
    this.querySelectorAll("[data-title-price]").forEach((box) => {
      const show = variant.compare_at_price && variant.compare_at_price > variant.price;
      box.hidden = !show;
      if (!show) return;
      const was = box.querySelector("[data-title-compare]");
      if (was) was.textContent = this.#formatMoney(variant.compare_at_price);
      const save = box.querySelector("[data-title-save]");
      const label = box.querySelector("[data-save-label]")?.dataset.saveLabel || "";
      const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
      if (save && label) save.textContent = label.replace("[pct]", String(pct));
    });
    const button = this.querySelector("[data-nappi-add]");
    if (button) {
      button.disabled = !variant.available;
      const label = button.querySelector("[data-nappi-add-label]");
      if (label) label.textContent = variant.available ? button.dataset.labelAdd : button.dataset.labelSoldOut;
    }
    this.#syncAvailability();
    this.#refreshTierPrices(variant.price);
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState(window.history.state, "", url.toString());
    if (variant.featured_media_id) {
      this.querySelectorAll("nappi-gallery").forEach((gallery) => gallery.goToMedia(variant.featured_media_id));
    }
    if (this.bundle) this.#renderBundle();
  }
  // Une valeur est « indisponible » si aucune variante disponible ne la porte
  // avec les autres options actuellement choisies.
  #syncAvailability() {
    const values = this.selectedValues;
    this.product.options.forEach((_, index) => {
      this.querySelectorAll(`input[data-option-position="${index + 1}"]`).forEach((radio) => {
        const test = values.slice();
        test[index] = radio.value;
        const match = this.product.variants.find(
          (v) => v.options.every((value, i) => i === index ? value === radio.value : values[i] === null || value === test[i])
        );
        const available = Boolean(match && match.available);
        const tile = radio.closest("[data-option-tile]");
        if (tile) tile.classList.toggle("is-unavailable", !available);
      });
    });
  }
  /*
   * Deux formats, comme en Liquid : `money` garde toujours les centimes,
   * `money_without_trailing_zeros` les supprime sur un montant rond. Le second
   * ne sert qu'au badge d'économie ; les prix, eux, doivent être rendus comme
   * côté serveur, sans quoi un palier affiché « 30,00 $ » au chargement se
   * réécrivait « 30 $ » au changement de taille — constaté le 20/09/2026.
   */
  /*
   * Gabarit monétaire relevé sur un prix RENDU PAR LE SERVEUR, avant toute
   * réécriture (21/09/2026).
   *
   * `Intl` avec la langue de la page ne reproduit pas le format de Shopify dès
   * que les deux divergent : sur le marché français, le serveur écrit
   * « €50,95 » quand `Intl.NumberFormat('en', { currency: 'EUR' })` rend
   * « €50.95 ». Le prix changeait donc de séparateur au premier changement de
   * taille. Aucune locale ne recrée ce format au hasard — on le relève.
   *
   * Le gabarit n'est retenu que s'il sait reproduire la référence au caractère
   * près. Sinon on retombe sur `Intl`, l'ancien comportement.
   */
  #gabaritMonnaie() {
    if (this.gabarit !== void 0) return this.gabarit;
    this.gabarit = null;
    const ref = this.querySelector("[data-tier-compare]")?.textContent.trim();
    const centimes = this.product.variants?.[0]?.price;
    const m = ref && ref.match(/^(\D*?)([\d.,\u00a0\u202f ]+)(\D*)$/);
    if (!m || !Number.isFinite(centimes)) return this.gabarit;
    const [, avant, nombre, apres] = m;
    const dec = /[.,](\d{2})$/.exec(nombre);
    const sepDec = dec ? nombre[nombre.length - 3] : "";
    const entier = dec ? nombre.slice(0, -3) : nombre;
    const sepMille = (entier.match(/[^\d]/) || [""])[0];
    const gabarit = { avant, apres, sepDec, sepMille };
    if (this.#avecGabarit(centimes, gabarit, false) !== ref) return this.gabarit;
    this.gabarit = gabarit;
    return this.gabarit;
  }
  #avecGabarit(cents, g, sansDecimales) {
    const signe = cents < 0 ? "-" : "";
    const abs = Math.abs(Math.round(cents));
    const entier = String(Math.floor(abs / 100));
    const groupe = g.sepMille ? entier.replace(/\B(?=(\d{3})+(?!\d))/g, g.sepMille) : entier;
    const centimes = String(abs % 100).padStart(2, "0");
    const chiffres = sansDecimales || !g.sepDec ? groupe : `${groupe}${g.sepDec}${centimes}`;
    return `${signe}${g.avant}${chiffres}${g.apres}`;
  }
  #formatMoney(cents, { trimZeros = false } = {}) {
    const amount = cents / 100;
    const whole = trimZeros && Number.isInteger(amount);
    const gabarit = this.#gabaritMonnaie();
    if (gabarit) return this.#avecGabarit(cents, gabarit, whole);
    try {
      return new Intl.NumberFormat(this.product.locale || "en-US", {
        style: "currency",
        currency: this.product.currency,
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return `${amount.toFixed(whole ? 0 : 2)}`;
    }
  }
}
class NappiGallery extends HTMLElement {
  #wheelSum = 0;
  #wheelLocked = false;
  #wheelTimer = 0;
  #wheelPrev = 0;
  #wheelRising = 0;
  #wheelAt = 0;
  #wheelDir = 0;
  connectedCallback() {
    this.viewport = this.querySelector("[data-gallery-viewport]");
    this.track = this.querySelector("[data-gallery-track]");
    this.slides = [...this.querySelectorAll("[data-gallery-slide]")];
    this.counter = this.querySelector("[data-gallery-current]");
    this.dots = [...this.querySelectorAll("[data-gallery-dot]")];
    this.index = 0;
    if (!this.track || this.slides.length === 0) return;
    this.querySelector("[data-gallery-prev]")?.addEventListener("click", () => this.go(this.index - 1));
    this.querySelector("[data-gallery-next]")?.addEventListener("click", () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener("click", () => this.go(i)));
    this.viewport.addEventListener("scroll", () => {
      if (!this.#isTouchLayout()) return;
      const i = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
      if (i !== this.index) this.#setIndex(i);
    }, { passive: true });
    this.slides.forEach((slide, i) => {
      slide.addEventListener("click", (event) => {
        if (event.target.closest("[data-modal-open]")) return;
        this.#openZoom(i);
      });
      slide.addEventListener("keydown", (event) => {
        if (event.target.closest("[data-modal-open]")) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.#openZoom(i);
        }
      });
    });
    this.addEventListener("keydown", (event) => {
      if (event.target.closest("[data-gallery-slide]") || event.target.closest("[data-gallery-nav]")) {
        if (event.key === "ArrowRight") this.go(this.index + 1);
        if (event.key === "ArrowLeft") this.go(this.index - 1);
      }
    });
    this.mq = window.matchMedia(this.dataset.touchQuery || "(max-width: 767px)");
    this.mq.addEventListener("change", () => this.go(this.index, true));
    this.#setIndex(0);
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.#isTouchLayout() && this.index !== 0) this.go(this.index, true);
    });
    this.resizeObserver.observe(this.viewport);
    this.addEventListener("wheel", (event) => this.#onWheel(event), { passive: false });
    if (window.Shopify?.designMode) {
      this.onBlockSelect = (event) => {
        const i = this.slides.findIndex((slide) => slide.contains(event.target));
        if (i >= 0) this.go(i, true);
      };
      document.addEventListener("shopify:block:select", this.onBlockSelect);
    }
  }
  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.onBlockSelect) document.removeEventListener("shopify:block:select", this.onBlockSelect);
  }
  #onWheel(event) {
    if (this.#isTouchLayout() || this.slides.length < 2) return;
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.viewport.clientWidth : 1;
    let dx = event.deltaX * unit;
    const dy = event.deltaY * unit;
    if (!dx && event.shiftKey) dx = dy;
    if (!dx || !event.shiftKey && Math.abs(dx) <= Math.abs(dy)) return;
    event.preventDefault();
    clearTimeout(this.#wheelTimer);
    this.#wheelTimer = setTimeout(() => {
      this.#wheelLocked = false;
      this.#wheelSum = 0;
    }, 200);
    const now = performance.now();
    const size = Math.abs(dx);
    const jump = size > this.#wheelPrev * 3 + 6;
    this.#wheelRising = size > this.#wheelPrev + 1 ? this.#wheelRising + 1 : 0;
    this.#wheelPrev = size;
    if (this.#wheelLocked) {
      const since = now - this.#wheelAt;
      const reversed = Math.sign(dx) !== this.#wheelDir && since > 250;
      const newFlick = (this.#wheelRising >= 2 || jump) && since > 450;
      if (!reversed && !newFlick) return;
      this.#wheelLocked = false;
      this.#wheelSum = 0;
    }
    this.#wheelSum += dx;
    if (Math.abs(this.#wheelSum) >= 40) {
      this.#wheelDir = Math.sign(this.#wheelSum);
      this.go(this.index + this.#wheelDir);
      this.#wheelLocked = true;
      this.#wheelSum = 0;
      this.#wheelAt = now;
    }
  }
  #isTouchLayout() {
    return this.mq ? this.mq.matches : false;
  }
  goToMedia(mediaId) {
    const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    if (i >= 0) this.go(i);
  }
  go(i, instant = false) {
    const count = this.slides.length;
    const loop = this.dataset.loop === "true";
    let next = i;
    if (loop) next = (i + count) % count;
    else next = Math.max(0, Math.min(count - 1, i));
    if (this.#isTouchLayout()) {
      this.track.style.transform = "";
      this.viewport.scrollTo({ left: next * this.viewport.clientWidth, behavior: instant || reducedMotion() ? "auto" : "smooth" });
    } else {
      const slide = this.slides[next];
      const offset = Math.abs(slide.offsetLeft - this.slides[0].offsetLeft);
      this.track.style.transition = instant || reducedMotion() ? "none" : "";
      this.track.style.transform = `translate3d(${this.dataset.direction === "rtl" ? offset : -offset}px, 0, 0)`;
    }
    this.#setIndex(next);
  }
  #setIndex(i) {
    this.index = i;
    if (this.counter) this.counter.textContent = String(i + 1);
    this.slides.forEach((slide, k) => {
      slide.classList.toggle("is-active", k === i);
      slide.setAttribute("aria-hidden", k === i ? "false" : "true");
    });
    this.dots.forEach((dot, k) => {
      dot.classList.toggle("is-active", k === i);
      dot.setAttribute("aria-current", k === i ? "true" : "false");
    });
    const prev = this.querySelector("[data-gallery-prev]");
    const next = this.querySelector("[data-gallery-next]");
    if (this.dataset.loop !== "true") {
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === this.slides.length - 1;
    }
  }
  #openZoom(i) {
    const modal = document.getElementById(this.dataset.zoomModal);
    if (!modal) return;
    modal.open();
    const target = modal.querySelector(`[data-zoom-index="${i}"]`);
    if (target) target.scrollIntoView({ block: "start" });
  }
}
class NappiModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    if (!this.dialog) return;
    this.dialog.addEventListener(this.dataset.backdropEvent || "click", (event) => {
      if (event.target === this.dialog || event.target.classList.contains("nappi-modal__frame")) this.close();
    });
    this.dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      this.close();
    });
    this.querySelectorAll("[data-modal-close]").forEach((btn) => btn.addEventListener("click", () => this.close()));
    this.#armerGlissement();
    if (this.dataset.hash) {
      const fromHash = () => {
        if (window.location.hash === `#${this.dataset.hash}`) this.open();
      };
      window.addEventListener("hashchange", fromHash);
      window.addEventListener("popstate", () => {
        if (this.dialog.open && window.location.hash !== `#${this.dataset.hash}`) this.close();
      });
      if (document.readyState === "complete") fromHash();
      else window.addEventListener("load", fromHash, { once: true });
    }
    if (!NappiModal.#delegated) {
      NappiModal.#delegated = true;
      document.addEventListener("click", (event) => {
        const opener = event.target.closest("[data-modal-open]");
        if (!opener) return;
        const modal = document.getElementById(opener.dataset.modalOpen);
        if (!modal) return;
        event.preventDefault();
        modal.open(opener);
        const tab = opener.dataset.modalTab;
        if (tab) modal.querySelector("nappi-tabs")?.select(tab);
      });
    }
  }
  static #delegated = false;
  /* Fermeture au glissement (21/09/2026), sur téléphone seulement : la feuille
     se tire vers le bas et part si on la lâche assez bas. Elle ne bouge que si
     le contenu est déjà en haut de son défilement — sinon on ferait glisser la
     fenêtre au lieu de lire le texte. */
  #armerGlissement() {
    const boite = this.dialog.querySelector(".nappi-modal__box");
    if (!boite) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const REPOS = 0;
    const SEUIL = 110;
    let debut = null;
    let ecart = 0;
    const haut = () => {
      const s = this.dialog.querySelector("[data-modal-scroll]");
      return !s || s.scrollTop <= 0;
    };
    boite.addEventListener("touchstart", (e) => {
      if (!mq.matches || e.touches.length !== 1 || !haut()) return;
      debut = e.touches[0].clientY;
      ecart = 0;
    }, { passive: true });
    boite.addEventListener("touchmove", (e) => {
      if (debut === null) return;
      const delta = e.touches[0].clientY - debut;
      if (delta <= 0 || !haut()) {
        if (ecart) {
          boite.classList.remove("is-dragging");
          boite.style.transform = "";
        }
        debut = null;
        ecart = 0;
        return;
      }
      ecart = delta;
      boite.classList.add("is-dragging");
      boite.style.transform = `translateY(${REPOS + delta}px)`;
      if (e.cancelable) e.preventDefault();
    }, { passive: false });
    const relacher = () => {
      if (debut === null) return;
      const partir = ecart > SEUIL;
      boite.classList.remove("is-dragging");
      boite.style.transform = "";
      debut = null;
      ecart = 0;
      if (partir) this.close();
    };
    boite.addEventListener("touchend", relacher);
    boite.addEventListener("touchcancel", relacher);
  }
  open(opener) {
    if (!this.dialog || this.dialog.open) return;
    clearTimeout(this.closeTimer);
    this.closing = false;
    this.opener = opener || document.activeElement;
    this.dialog.showModal();
    if (this.dialog.hasAttribute("tabindex")) this.dialog.focus({ preventScroll: true });
    document.documentElement.classList.add("nappi-scroll-lock");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.dialog.classList.add("is-open"));
    });
    const scroller = this.dialog.querySelector("[data-modal-scroll]");
    if (scroller) scroller.scrollTop = 0;
  }
  close() {
    if (!this.dialog || !this.dialog.open || this.closing) return;
    this.closing = true;
    this.dialog.classList.remove("is-open");
    const done = () => {
      if (!this.closing) return;
      this.closing = false;
      this.dialog.close();
      document.documentElement.classList.remove("nappi-scroll-lock");
      if (this.dataset.hash && window.location.hash === `#${this.dataset.hash}`) {
        const url = new URL(window.location.href);
        url.hash = "";
        window.history.replaceState(window.history.state, "", url.toString());
      }
      if (this.opener && typeof this.opener.focus === "function") this.opener.focus({ preventScroll: true });
      this.onClosed?.();
    };
    const duration = reducedMotion() ? 0 : Number(this.dataset.closeDuration || 250);
    if (duration) this.closeTimer = setTimeout(done, duration);
    else done();
  }
}
class NappiSizeGuide extends NappiModal {
  connectedCallback() {
    this.tables = [...this.querySelectorAll("[data-sg-table]")].map((wrap) => ({
      wrap,
      box: wrap.querySelector("[data-sg-table-box]"),
      table: wrap.querySelector("table"),
      hint: wrap.querySelector("[data-sg-swipe]"),
      dismissed: false,
      timer: 0
    }));
    this.tables.forEach((entry) => {
      if (!entry.table || !entry.hint) return;
      const dismiss = () => this.#hideHint(entry, true);
      entry.table.addEventListener("touchstart", dismiss, { passive: true });
      entry.table.addEventListener("wheel", dismiss, { passive: true });
    });
    this.onResize = () => this.tables.forEach((entry) => this.#syncHint(entry));
    this.addEventListener("keydown", (event) => {
      if (event.key !== "Tab" || !this.dialog?.open) return;
      const list = this.#tabStops();
      const index = list.indexOf(document.activeElement);
      const step = event.shiftKey ? -1 : 1;
      const next = index < 0 ? list[event.shiftKey ? list.length - 1 : 0] : list[(index + step + list.length) % list.length];
      event.preventDefault();
      next.focus();
    });
    this.querySelectorAll(".nappi-sg__img--full").forEach((img) => {
      if (img.complete && img.naturalWidth) return;
      img.classList.add("is-pending");
      const reveal = () => img.classList.remove("is-pending");
      img.addEventListener("load", reveal, { once: true });
      img.addEventListener("error", reveal, { once: true });
    });
    document.addEventListener("shopify:block:select", (event) => {
      if (this.contains(event.target)) this.open();
    });
    document.addEventListener("shopify:block:deselect", (event) => {
      if (this.contains(event.target)) this.close();
    });
    super.connectedCallback();
  }
  open(opener) {
    if (!this.dialog || this.dialog.open) return;
    super.open(opener);
    this.tables.forEach((entry) => {
      if (entry.box) entry.box.scrollLeft = 0;
      entry.dismissed = false;
      this.#syncHint(entry);
    });
    window.addEventListener("resize", this.onResize);
  }
  onClosed() {
    window.removeEventListener("resize", this.onResize);
    this.tables.forEach((entry) => {
      clearTimeout(entry.timer);
      if (!entry.hint) return;
      entry.hint.classList.remove("is-visible");
      entry.hint.hidden = true;
    });
  }
  // Arrêts de tabulation : bouton de fermeture, liens du texte, puis les zones
  // qui défilent sans contenu focalisable (tableaux qui débordent, sinon la zone
  // de contenu), comme les navigateurs les rendent atteignables au clavier.
  #tabStops() {
    const close = this.querySelector("[data-modal-close]");
    const scroller = this.querySelector("[data-modal-scroll]");
    const stops = [close];
    if (!scroller) return stops;
    const inner = [...scroller.querySelectorAll("a[href], button, [data-sg-table-box]")].filter((el) => {
      if (el.matches("[data-sg-table-box]")) return el.scrollWidth - el.clientWidth > 1;
      return el.getClientRects().length > 0;
    });
    if (inner.length) stops.push(...inner);
    else if (scroller.scrollHeight - scroller.clientHeight > 1) stops.push(scroller);
    return stops;
  }
  #syncHint(entry) {
    if (!entry.hint || !entry.box || entry.dismissed) return;
    const overflows = entry.box.scrollWidth - entry.box.clientWidth > 1;
    if (!overflows) {
      this.#hideHint(entry, false);
      return;
    }
    if (!entry.hint.hidden && entry.hint.classList.contains("is-visible")) return;
    clearTimeout(entry.timer);
    entry.hint.hidden = false;
    void entry.hint.offsetWidth;
    entry.hint.classList.add("is-visible");
  }
  #hideHint(entry, dismissed) {
    if (!entry.hint) return;
    if (dismissed) entry.dismissed = true;
    if (entry.hint.hidden) return;
    entry.hint.classList.remove("is-visible");
    clearTimeout(entry.timer);
    const duration = dismissed && !reducedMotion() ? 200 : 0;
    entry.timer = setTimeout(() => {
      entry.hint.hidden = true;
    }, duration);
  }
}
class NappiSizeFinder extends NappiModal {
  static ORDER = ["who", "shoe", "calf", "result"];
  // Bornes des curseurs, plus larges que les données pour laisser répondre
  // « je chausse plus petit / plus grand » ; le résultat le signale alors.
  static BOUNDS = {
    us: { women: [4, 14], men: [4, 16] },
    eu: { women: [34, 50], men: [34, 50] },
    uk: { women: [2, 12], men: [3, 15] }
  };
  static START = {
    us: { women: 8, men: 9.5 },
    eu: { women: 39, men: 43 },
    uk: { women: 6, men: 8.5 }
  };
  static UNIT_LABEL = { us: "US", eu: "EU", uk: "UK" };
  // Phrases du script en anglais, par défaut ; la page fournit celles de sa
  // langue (bloc data-sf-i18n, clés nappi.size_finder.* des fichiers de
  // langue, 03/10/2026). Les trous [size], [base], [unit]… sont remplis par #t.
  static TEXT = {
    next: "Next",
    select_size: "Select [size]",
    close: "Close",
    fit_best: "Best match",
    fit_good: "Good match",
    fit_closest: "Closest match",
    why_women: "Based on a women's [unit] [shoe][calf].",
    why_men: "Based on a men's [unit] [shoe][calf].",
    calf_cm: " and a [value] cm calf",
    calf_in: " and a [value] in calf",
    calf_shape_slim: " and slimmer calves",
    calf_shape_average: " and average calves",
    calf_shape_full: " and fuller calves",
    note_shape_up: "Your foot sits at the top of [base], and fuller calves are more comfortable in [size].",
    note_calf_up: "We went up from [base] so the calf isn't too tight.",
    note_calf_down: "We went down from [base] so the calf keeps a firm hold.",
    note_calf_over: "Your calf is above the range of our largest size: expect a very firm hold.",
    note_calf_under: "Your calf is below the range of our smallest size: the hold will be lighter.",
    note_calf_slim: "Your calf is slimmer than [size]'s range: the hold will be a little lighter, but this is the size that fits your foot.",
    note_above: "That's above our largest size. [size] is the closest, and may feel snug on the foot.",
    note_below: "That's below our smallest size. [size] is the closest.",
    spec_us_women: "US women's",
    spec_us_men: "US men's",
    spec_eu: "EU",
    spec_calf: "Calf",
    specs_title: "[size] is made for",
    in_suffix: "in"
  };
  // Mouvement repris du tiroir panier (nappi-cart.css) : 300 ms, même courbe
  static MOTION = { duration: 300, easing: "cubic-bezier(0.25, 1, 0.25, 1)" };
  static parse(raw) {
    const range = (txt) => {
      const clean = String(txt || "").replace(/½/g, ".5").replace(/\s+/g, "");
      const m = /^(\d+(?:\.\d+)?)[-–](\d+(?:\.\d+)?)$/.exec(clean);
      return m ? [Number(m[1]), Number(m[2])] : null;
    };
    return raw.split(/\r?\n/).map((line) => line.split("|").map((cell) => cell.trim())).filter((cells) => cells.length >= 7 && cells[0]).map((c) => ({
      size: c[0],
      us: { women: range(c[1]), men: range(c[2]) },
      eu: { women: range(c[3]), men: range(c[3]) },
      uk: { women: range(c[4]), men: range(c[5]) },
      calf: range(c[6])
    }));
  }
  // 8.5 → « 8½ » ; les tailles et mollets n'ont que des demi-points
  static fmt(value) {
    const whole = Math.floor(value);
    return value - whole >= 0.5 ? `${whole}½` : String(whole);
  }
  static fmtRange(range) {
    return range ? `${NappiSizeFinder.fmt(range[0])}–${NappiSizeFinder.fmt(range[1])}` : "";
  }
  connectedCallback() {
    const decode = (value) => {
      const area = document.createElement("textarea");
      area.innerHTML = value;
      return area.value;
    };
    try {
      const raw = JSON.parse(this.querySelector("[data-sf-i18n]")?.textContent || "{}") || {};
      this.i18n = Object.fromEntries(Object.entries(raw).map(([key, value]) => [key, typeof value === "string" ? decode(value) : value]));
    } catch (error) {
      this.i18n = {};
    }
    this.sizes = NappiSizeFinder.parse(this.dataset.sizes || "");
    const country = (this.dataset.country || "").toUpperCase();
    const unit = ["US", "CA", "AU", "NZ"].includes(country) ? "us" : ["GB", "IE"].includes(country) ? "uk" : country ? "eu" : "us";
    this.state = {
      step: "who",
      who: null,
      unit,
      shoe: null,
      calfShape: null,
      measuring: false,
      calfUnit: unit === "us" ? "in" : "cm",
      calfIn: 14
    };
    this.$ = (sel) => this.querySelector(sel);
    this.shoeRange = this.$("[data-sf-shoe]");
    this.calfRange = this.$("[data-sf-calf]");
    this.next = this.$("[data-sf-next]");
    this.querySelectorAll("[data-sf-who]").forEach((btn) => btn.addEventListener("click", () => {
      this.state.who = btn.dataset.sfWho;
      this.state.shoe = null;
      this.#press("[data-sf-who]", btn);
      this.#advance("shoe");
    }));
    this.querySelectorAll("[data-sf-unit]").forEach((btn) => btn.addEventListener("click", () => {
      if (this.state.unit === btn.dataset.sfUnit) return;
      this.state.unit = btn.dataset.sfUnit;
      this.state.shoe = null;
      this.#syncShoe();
    }));
    this.shoeRange.addEventListener("input", () => {
      this.state.shoe = Number(this.shoeRange.value);
      this.#syncShoe();
    });
    this.querySelectorAll("[data-sf-shoe-step]").forEach((btn) => btn.addEventListener("click", () => {
      const [min, max] = this.#bounds();
      const value = (this.state.shoe ?? Number(this.shoeRange.value)) + 0.5 * Number(btn.dataset.sfShoeStep);
      this.state.shoe = Math.min(max, Math.max(min, value));
      this.#syncShoe();
    }));
    this.querySelectorAll("[data-sf-calf-shape]").forEach((btn) => btn.addEventListener("click", () => {
      this.state.calfShape = btn.dataset.sfCalfShape;
      this.state.measuring = false;
      this.#press("[data-sf-calf-shape]", btn);
      this.#syncMeasure();
      this.#advance("result");
    }));
    this.$("[data-sf-measure-toggle]").addEventListener("click", () => {
      this.state.measuring = !this.state.measuring;
      if (this.state.measuring) {
        this.state.calfShape = null;
        this.#press("[data-sf-calf-shape]", null);
      }
      this.#syncMeasure();
      this.#syncNext();
      if (this.state.measuring) {
        this.$("[data-sf-measure]").scrollIntoView({ block: "nearest", behavior: reducedMotion() ? "auto" : "smooth" });
      }
    });
    this.calfRange.addEventListener("input", () => {
      const v = Number(this.calfRange.value);
      this.state.calfIn = this.state.calfUnit === "in" ? v : v / 2.54;
      this.#syncCalf();
    });
    this.querySelectorAll("[data-sf-calf-step]").forEach((btn) => btn.addEventListener("click", () => {
      const dir = Number(btn.dataset.sfCalfStep);
      const inches = this.state.calfUnit === "in" ? this.state.calfIn + 0.5 * dir : (Math.round(this.state.calfIn * 2.54) + dir) / 2.54;
      this.state.calfIn = Math.min(22, Math.max(8, inches));
      this.#syncCalf();
    }));
    this.querySelectorAll("[data-sf-calf-unit]").forEach((btn) => btn.addEventListener("click", () => {
      this.state.calfUnit = btn.dataset.sfCalfUnit;
      this.#syncCalf();
    }));
    this.box = this.$(".nappi-modal__box");
    const onRelease = () => {
      const offset = this.box.style.transform;
      if (!offset) return;
      this.dragFrom = offset;
      requestAnimationFrame(() => {
        if (this.closing || !this.dialog.open || !this.dragFrom) return;
        const from = this.dragFrom;
        this.dragFrom = null;
        this.anim?.cancel();
        this.anim = this.box.animate([{ transform: from }, { transform: "none" }], NappiSizeFinder.MOTION);
      });
    };
    this.box.addEventListener("touchend", onRelease);
    this.box.addEventListener("touchcancel", onRelease);
    [this.shoeRange, this.calfRange].forEach((range) => {
      range.addEventListener("touchstart", (event) => event.stopPropagation(), { passive: true });
    });
    this.$("[data-sf-back]").addEventListener("click", () => {
      const i = NappiSizeFinder.ORDER.indexOf(this.state.step);
      if (i <= 0) this.close();
      else this.#go(NappiSizeFinder.ORDER[i - 1]);
    });
    this.next.addEventListener("click", () => this.#onNext());
    this.$("[data-sf-restart]").addEventListener("click", () => this.#go("who"));
    this.$("[data-sf-chart]").addEventListener("click", () => {
      const chart = document.getElementById(this.dataset.chart);
      this.close();
      if (!chart) return;
      const wait = reducedMotion() ? 0 : Number(this.dataset.closeDuration || 250) + 20;
      setTimeout(() => chart.open(this.opener), wait);
    });
    super.connectedCallback();
  }
  /* Ouverture et fermeture propres au Fit Finder (01/10/2026, 3e retour
     d'Erevan : « on n'a pas d'animation d'ouverture », « faire comme le
     tiroir panier »). <nappi-modal> anime par une transition CSS lancée deux
     trames après showModal() : quand une trame saute, la fenêtre apparaît
     d'un coup. Ici, comme le tiroir panier, l'animation part au moment même
     de l'ouverture (Web Animations), depuis la droite sur ordinateur et
     depuis le bas sur téléphone. Le voile suit en CSS (nappi-product.css). */
  #offscreen() {
    return window.matchMedia("(min-width: 768px)").matches ? "translateX(100%)" : "translateY(100%)";
  }
  open(opener) {
    if (!this.dialog || this.dialog.open) return;
    this.#go("who", false);
    clearTimeout(this.closeTimer);
    this.closing = false;
    this.dragFrom = null;
    this.opener = opener || document.activeElement;
    this.anim?.cancel();
    this.dialog.classList.remove("is-closing");
    this.dialog.showModal();
    this.dialog.classList.add("is-open");
    this.dialog.focus({ preventScroll: true });
    document.documentElement.classList.add("nappi-scroll-lock");
    if (!reducedMotion()) {
      this.anim = this.box.animate([{ transform: this.#offscreen() }, { transform: "none" }], NappiSizeFinder.MOTION);
    }
  }
  close() {
    if (!this.dialog || !this.dialog.open || this.closing) return;
    this.closing = true;
    const from = this.dragFrom || "none";
    this.dragFrom = null;
    this.dialog.classList.remove("is-open");
    this.dialog.classList.add("is-closing");
    const done = () => {
      if (!this.closing) return;
      this.closing = false;
      clearTimeout(this.closeTimer);
      this.anim?.cancel();
      this.dialog.classList.remove("is-closing");
      this.dialog.close();
      document.documentElement.classList.remove("nappi-scroll-lock");
      if (this.opener && typeof this.opener.focus === "function") this.opener.focus({ preventScroll: true });
    };
    if (reducedMotion()) {
      done();
      return;
    }
    this.anim?.cancel();
    this.anim = this.box.animate(
      [{ transform: from }, { transform: this.#offscreen() }],
      { ...NappiSizeFinder.MOTION, fill: "forwards" }
    );
    this.anim.finished.then(done, done);
    this.closeTimer = setTimeout(done, NappiSizeFinder.MOTION.duration + 200);
  }
  #press(selector, active) {
    this.querySelectorAll(selector).forEach((btn) => btn.setAttribute("aria-pressed", String(btn === active)));
  }
  #advance(step) {
    clearTimeout(this.advanceTimer);
    this.advanceTimer = setTimeout(() => this.#go(step), reducedMotion() ? 0 : 160);
  }
  #go(step, focus = true) {
    this.state.step = step;
    const index = NappiSizeFinder.ORDER.indexOf(step);
    this.querySelectorAll("[data-sf-step]").forEach((el) => {
      el.hidden = el.dataset.sfStep !== step;
    });
    this.$("[data-sf-progress]").style.width = `${(index + 1) / NappiSizeFinder.ORDER.length * 100}%`;
    const back = this.$("[data-sf-back-label]");
    back.textContent = index === 0 ? back.dataset.close : back.dataset.back;
    this.$("[data-sf-restart]").hidden = step !== "result";
    if (step === "shoe") this.#syncShoe();
    if (step === "calf") this.#syncMeasure();
    if (step === "result") this.#renderResult();
    this.#syncNext();
    const scroller = this.$("[data-modal-scroll]");
    if (scroller) scroller.scrollTop = 0;
    if (focus) this.querySelector(`[data-sf-step="${step}"] .nappi-sf__title`)?.focus({ preventScroll: true });
  }
  // Phrase dans la langue de la page, trous remplis ([size] → M…)
  #t(key, vars = {}) {
    let text = this.i18n?.[key] || NappiSizeFinder.TEXT[key] || "";
    Object.entries(vars).forEach(([name, value]) => {
      text = text.split(`[${name}]`).join(value);
    });
    return text;
  }
  #syncNext() {
    const { step } = this.state;
    let label = this.#t("next");
    let enabled = true;
    if (step === "who") enabled = Boolean(this.state.who);
    if (step === "calf") enabled = this.state.measuring || Boolean(this.state.calfShape);
    if (step === "result") label = this.result ? this.#t("select_size", { size: this.result.size }) : this.#t("close");
    this.next.textContent = label;
    this.next.disabled = !enabled;
  }
  #onNext() {
    const { step } = this.state;
    if (step === "who" && this.state.who) this.#go("shoe");
    else if (step === "shoe") this.#go("calf");
    else if (step === "calf") this.#go("result");
    else if (step === "result") this.#select();
  }
  #bounds() {
    return NappiSizeFinder.BOUNDS[this.state.unit][this.state.who || "women"];
  }
  #syncShoe() {
    const who = this.state.who || "women";
    const [min, max] = this.#bounds();
    if (this.state.shoe == null) this.state.shoe = NappiSizeFinder.START[this.state.unit][who];
    this.state.shoe = Math.min(max, Math.max(min, this.state.shoe));
    this.shoeRange.min = min;
    this.shoeRange.max = max;
    this.shoeRange.value = this.state.shoe;
    const unit = NappiSizeFinder.UNIT_LABEL[this.state.unit];
    const text = `${unit} ${NappiSizeFinder.fmt(this.state.shoe)}`;
    this.$("[data-sf-shoe-value]").textContent = text;
    this.shoeRange.setAttribute("aria-valuetext", `${who === "men" ? "Men's" : "Women's"} ${text}`);
    this.shoeRange.style.setProperty("--fill", `${(this.state.shoe - min) / (max - min) * 100}%`);
    this.#press("[data-sf-unit]", this.querySelector(`[data-sf-unit="${this.state.unit}"]`));
  }
  #syncMeasure() {
    const open = this.state.measuring;
    this.$("[data-sf-measure]").hidden = !open;
    this.$("[data-sf-measure-toggle]").setAttribute("aria-expanded", String(open));
    if (open) this.#syncCalf();
  }
  #syncCalf() {
    const inches = this.state.calfIn;
    const cm = this.state.calfUnit === "cm";
    const [min, max, step] = cm ? [20, 56, 1] : [8, 22, 0.5];
    const value = cm ? Math.round(inches * 2.54) : Math.round(inches * 2) / 2;
    this.calfRange.min = min;
    this.calfRange.max = max;
    this.calfRange.step = step;
    this.calfRange.value = value;
    const text = cm ? `${value} cm` : `${NappiSizeFinder.fmt(value)} ${this.#t("in_suffix")}`;
    this.$("[data-sf-calf-value]").textContent = text;
    this.calfRange.setAttribute("aria-valuetext", text);
    this.calfRange.style.setProperty("--fill", `${(value - min) / (max - min) * 100}%`);
    this.#press("[data-sf-calf-unit]", this.querySelector(`[data-sf-calf-unit="${this.state.calfUnit}"]`));
  }
  // Taille recommandée : { size, index, fit, notes }
  #recommend() {
    const { who, unit, shoe } = this.state;
    const sizes = this.sizes;
    if (!sizes.length || !who || shoe == null) return null;
    const col = (s) => s[unit][who];
    const defined = sizes.map((s, i) => [i, col(s)]).filter(([, r]) => r);
    let index = -1;
    let outside = 0;
    for (const [i, r] of defined) {
      if (shoe >= r[0] && shoe <= r[1]) {
        index = i;
        break;
      }
    }
    if (index < 0) {
      if (!defined.length) index = 0;
      else if (shoe < defined[0][1][0]) {
        index = 0;
        if (defined[0][0] === 0) outside = -1;
      } else if (shoe > defined[defined.length - 1][1][1]) {
        index = sizes.length - 1;
        outside = 1;
      } else {
        index = defined.find(([, r]) => r[0] > shoe)[0];
      }
    }
    const base = index;
    const range = col(sizes[base]);
    let reason = "";
    if (this.state.measuring) {
      const calf = this.state.calfIn;
      const c = sizes[base].calf;
      if (c && calf > c[1]) {
        const up = sizes.findIndex((s, i) => i > base && s.calf && calf >= s.calf[0] && calf <= s.calf[1]);
        if (up >= 0) {
          index = up;
          reason = "calf-up";
        } else if (base < sizes.length - 1) {
          index = sizes.length - 1;
          reason = "calf-over";
        } else reason = "calf-over";
      } else if (c && calf < c[0]) {
        let down = -1;
        for (let i = base - 1; i >= 0; i -= 1) {
          const sc = sizes[i].calf;
          if (sc && calf >= sc[0] && calf <= sc[1]) {
            down = i;
            break;
          }
        }
        const below = down >= 0 ? col(sizes[down]) : null;
        const footFits = down >= 0 && (below ? shoe <= below[1] + 0.5 : Boolean(range) && shoe <= range[0]);
        if (footFits) {
          index = down;
          reason = "calf-down";
        } else reason = calf < (sizes[0].calf ? sizes[0].calf[0] : 0) ? "calf-under" : "calf-slim";
      }
    } else if (range && !outside) {
      if (this.state.calfShape === "full" && shoe >= range[1] - 0.5 && base < sizes.length - 1) {
        index = base + 1;
        reason = "shape-up";
      }
    }
    let fit = "best";
    if (outside || reason === "calf-over" || reason === "calf-under") fit = "closest";
    else if (reason) fit = "good";
    return { size: sizes[index].size, index, base: sizes[base].size, fit, reason, outside };
  }
  #renderResult() {
    const result = this.#recommend();
    this.result = result;
    if (!result) return;
    const { who, unit, shoe } = this.state;
    const F = NappiSizeFinder;
    this.$("[data-sf-size]").textContent = result.size;
    this.$("[data-sf-fit]").textContent = this.#t(`fit_${result.fit}`);
    this.$("[data-sf-fit]").dataset.fit = result.fit;
    let calfText = "";
    if (this.state.measuring) {
      calfText = this.state.calfUnit === "cm" ? this.#t("calf_cm", { value: Math.round(this.state.calfIn * 2.54) }) : this.#t("calf_in", { value: F.fmt(Math.round(this.state.calfIn * 2) / 2) });
    } else if (this.state.calfShape) {
      calfText = this.#t(`calf_shape_${this.state.calfShape}`);
    }
    this.$("[data-sf-why]").textContent = this.#t(who === "men" ? "why_men" : "why_women", {
      unit: F.UNIT_LABEL[unit],
      shoe: F.fmt(shoe),
      calf: calfText
    });
    const sizes = { size: result.size, base: result.base };
    const notes = {
      "shape-up": "note_shape_up",
      "calf-up": "note_calf_up",
      "calf-down": "note_calf_down",
      "calf-over": "note_calf_over",
      "calf-under": "note_calf_under",
      "calf-slim": "note_calf_slim"
    };
    let note = notes[result.reason] ? this.#t(notes[result.reason], sizes) : "";
    if (result.outside === 1) note = this.#t("note_above", sizes);
    if (result.outside === -1) note = this.#t("note_below", sizes);
    const noteEl = this.$("[data-sf-note]");
    noteEl.textContent = note;
    noteEl.hidden = !note;
    const s = this.sizes[result.index];
    const rows = [];
    if (s.us.women) rows.push([this.#t("spec_us_women"), F.fmtRange(s.us.women)]);
    if (s.us.men) rows.push([this.#t("spec_us_men"), F.fmtRange(s.us.men)]);
    if (s.eu.women) rows.push([this.#t("spec_eu"), F.fmtRange(s.eu.women)]);
    if (s.calf) {
      rows.push([this.#t("spec_calf"), this.state.calfUnit === "cm" ? `${Math.round(s.calf[0] * 2.54)}–${Math.round(s.calf[1] * 2.54)} cm` : `${F.fmtRange(s.calf)} ${this.#t("in_suffix")}`]);
    }
    const dl = this.$("[data-sf-specs]");
    dl.replaceChildren(...rows.flatMap(([k, v]) => {
      const dt = document.createElement("dt");
      dt.textContent = k;
      const dd = document.createElement("dd");
      dd.textContent = v;
      return [dt, dd];
    }));
    this.$("[data-sf-specs-title]").textContent = this.#t("specs_title", { size: result.size });
  }
  // Coche la taille recommandée sur la fiche, puis ferme
  #select() {
    if (!this.result) {
      this.close();
      return;
    }
    const scope = this.closest(".shopify-section") || document;
    const value = window.CSS && CSS.escape ? CSS.escape(this.result.size) : this.result.size.replace(/"/g, '\\"');
    const input = scope.querySelector(`.nappi-product__tile input[value="${value}"]`);
    if (input && !input.checked) input.click();
    this.close();
  }
}
class NappiFitQuiz extends NappiModal {
  static MOTION = { duration: 300, easing: "cubic-bezier(0.25, 1, 0.25, 1)" };
  /* Pointure EU pour une pointure US (tables usuelles des fabricants). */
  static EU = {
    women: { 4: 34.5, 4.5: 35, 5: 35.5, 5.5: 36, 6: 36.5, 6.5: 37, 7: 37.5, 7.5: 38, 8: 38.5, 8.5: 39, 9: 40, 9.5: 40.5, 10: 41, 10.5: 42, 11: 42.5, 11.5: 43, 12: 44, 12.5: 44.5, 13: 45 },
    men: { 6: 39, 6.5: 39.5, 7: 40, 7.5: 40.5, 8: 41, 8.5: 42, 9: 42.5, 9.5: 43, 10: 44, 10.5: 44.5, 11: 45, 11.5: 45.5, 12: 46, 12.5: 46.5, 13: 47 }
  };
  connectedCallback() {
    super.connectedCallback();
    if (!this.dialog) return;
    try {
      this.t = JSON.parse(this.querySelector("[data-fq-i18n]")?.textContent || "{}");
    } catch (e) {
      this.t = {};
    }
    const area = document.createElement("textarea");
    Object.keys(this.t).forEach((key) => {
      area.innerHTML = this.t[key];
      this.t[key] = area.value;
    });
    this.sizes = (this.dataset.sizes || "").split(/\n/).map((line) => line.split("|").map((part) => part.trim())).filter((parts) => parts[0]).map(([size, women, men]) => ({ size, women: this.#range(women), men: this.#range(men) }));
    this.steps = [...this.querySelectorAll("[data-fq-step]")];
    this.box = this.querySelector("[data-fq-box]");
    const country = (this.dataset.country || "").toUpperCase();
    this.system = { CA: "ca", GB: "uk", IE: "uk", AU: "au", NZ: "au", US: "us" }[country] || (country ? "eu" : "us");
    this.#reset();
    this.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const pick = target.closest("[data-fq-pick]");
      if (pick) return this.#pick(pick);
      const multi = target.closest("[data-fq-multi]");
      if (multi) return this.#toggle(multi);
      const tab = target.closest("[data-fq-tab]");
      if (tab) return this.#showGrid(tab.dataset.fqTab);
      const system = target.closest("[data-fq-system]");
      if (system) {
        this.system = system.dataset.fqSystem;
        return this.#showGrid(this.answers.shoeGrid);
      }
      if (target.closest("[data-fq-prev]")) return this.#go(this.step - 1);
      if (target.closest("[data-fq-continue]")) return this.#go(7);
      if (target.closest("[data-fq-retake]")) {
        this.#reset();
        return this.#go(1);
      }
      if (target.closest("[data-fq-select]")) return this.#select();
      if (target.closest("[data-fq-chart]")) {
        const chart = document.getElementById(this.dataset.chart);
        this.close();
        if (chart) setTimeout(() => chart.open(), 260);
      }
    });
  }
  /* « 8-10.5 » → { min: 8, max: 10.5 } ; « - » → null (pas de taille). */
  #range(text) {
    const m = /^([\d.]+)\s*-\s*([\d.]+)$/.exec(text || "");
    return m ? { min: Number(m[1]), max: Number(m[2]) } : null;
  }
  /* Pointure US → pointure du système choisi (CA = US ; UK : femme −2,
     homme −1 ; AU : femme = US, homme −1 ; EU : table). */
  #convert(us, gender) {
    switch (this.system) {
      case "uk":
        return us - (gender === "men" ? 1 : 2);
      case "au":
        return gender === "men" ? us - 1 : us;
      case "eu":
        return NappiFitQuiz.EU[gender][us] ?? Math.round((us + (gender === "men" ? 33 : 31)) * 2) / 2;
      default:
        return us;
    }
  }
  #num(n) {
    return Number.isInteger(n) ? String(n) : `${Math.floor(n)}½`;
  }
  #pretty(range, gender) {
    return `${this.#num(this.#convert(range.min, gender))}–${this.#num(this.#convert(range.max, gender))}`;
  }
  /* Format tiroir (01/11/2026) : même mouvement que l'ancien assistant et le
     tiroir panier, lancé au moment de l'ouverture (Web Animations), depuis la
     droite en desktop et depuis le bas en mobile. */
  #offscreen() {
    return window.matchMedia("(min-width: 768px)").matches ? "translateX(100%)" : "translateY(100%)";
  }
  #reset() {
    this.answers = { who: "women", cond: [] };
    this.querySelectorAll('[aria-pressed="true"]').forEach((b) => {
      if (!b.matches("[data-fq-tab]")) b.setAttribute("aria-pressed", "false");
    });
    this.#showGrid("women");
    this.step = 1;
    this.#render();
  }
  open(opener) {
    if (!this.dialog || this.dialog.open) return;
    clearTimeout(this.closeTimer);
    this.closing = false;
    this.opener = opener || document.activeElement;
    this.anim?.cancel();
    this.dialog.classList.remove("is-closing");
    this.dialog.showModal();
    this.dialog.classList.add("is-open");
    this.dialog.focus({ preventScroll: true });
    document.documentElement.classList.add("nappi-scroll-lock");
    if (!reducedMotion()) {
      this.anim = this.box.animate([{ transform: this.#offscreen() }, { transform: "none" }], NappiFitQuiz.MOTION);
    }
    this.#render();
  }
  close() {
    if (!this.dialog || !this.dialog.open || this.closing) return;
    this.closing = true;
    this.dialog.classList.remove("is-open");
    this.dialog.classList.add("is-closing");
    const done = () => {
      if (!this.closing) return;
      this.closing = false;
      clearTimeout(this.closeTimer);
      this.anim?.cancel();
      this.dialog.classList.remove("is-closing");
      this.dialog.close();
      document.documentElement.classList.remove("nappi-scroll-lock");
      if (this.opener && typeof this.opener.focus === "function") this.opener.focus({ preventScroll: true });
    };
    if (reducedMotion()) {
      done();
      return;
    }
    this.anim?.cancel();
    const from = this.box.style.transform || "none";
    this.anim = this.box.animate([{ transform: from }, { transform: this.#offscreen() }], { ...NappiFitQuiz.MOTION, fill: "forwards" });
    this.anim.finished.then(done, done);
    this.closeTimer = setTimeout(done, NappiFitQuiz.MOTION.duration + 200);
  }
  #pick(button) {
    const key = button.dataset.fqPick;
    this.answers[key] = button.dataset.value;
    button.parentElement.querySelectorAll("[data-fq-pick]").forEach((b) => b.setAttribute("aria-pressed", String(b === button)));
    if (key === "who") this.#showGrid(button.dataset.value);
    if (key === "brand") {
      const title = this.querySelector("[data-fq-shoe-title]");
      if (title) title.textContent = button.dataset.value ? (this.t.q2_title_brand || "").replace("[brand]", button.dataset.value) : title.dataset.default;
    }
    clearTimeout(this.advance);
    this.advance = setTimeout(() => this.#go(this.step + 1), reducedMotion() ? 0 : 180);
  }
  #toggle(button) {
    const value = button.dataset.value;
    const group = [...this.querySelectorAll("[data-fq-multi]")];
    let list = this.answers.cond.filter((v) => v !== value);
    if (!this.answers.cond.includes(value)) {
      list = value === "none" ? ["none"] : [...list.filter((v) => v !== "none"), value];
    }
    this.answers.cond = list;
    group.forEach((b) => b.setAttribute("aria-pressed", String(list.includes(b.dataset.value))));
    this.#render();
  }
  #showGrid(who) {
    this.answers.shoeGrid = who;
    this.querySelectorAll("[data-fq-tab]").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.fqTab === who)));
    this.querySelectorAll("[data-fq-sizes]").forEach((grid) => {
      grid.hidden = grid.dataset.fqSizes !== who;
      grid.querySelectorAll('[data-fq-pick="shoe"]').forEach((tile) => {
        tile.textContent = this.#num(this.#convert(Number(tile.dataset.value), grid.dataset.fqSizes));
      });
    });
    this.querySelectorAll("[data-fq-system]").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.fqSystem === this.system)));
    const label = this.system.toUpperCase();
    const sub = this.querySelector("[data-fq-shoe-sub]");
    if (sub) sub.textContent = (who === "men" ? this.t.q2_sub_men || "" : this.t.q2_sub_women || "").replace("[system]", label);
  }
  #go(step) {
    if (step < 1) return;
    if (step > 7) {
      this.step = "result";
      this.#result();
    } else {
      this.step = step;
    }
    this.#render();
    const current = this.steps.find((s) => !s.hidden);
    const scroller = this.querySelector("[data-modal-scroll]");
    if (scroller) scroller.scrollTop = 0;
    current?.querySelector(".nappi-fq__q")?.focus({ preventScroll: true });
  }
  #render() {
    const key = String(this.step);
    this.steps.forEach((s) => {
      s.hidden = s.dataset.fqStep !== key;
    });
    const result = this.step === "result";
    this.box.classList.toggle("is-result", result);
    const n = result ? 8 : this.step;
    this.querySelectorAll("[data-fq-dot]").forEach((dot) => {
      const i = Number(dot.dataset.fqDot);
      dot.classList.toggle("is-done", i <= n);
      dot.classList.toggle("is-current", i === n);
    });
    const takes = this.querySelector("[data-fq-takes]");
    if (takes && !result) takes.textContent = (this.t.takes || "").replace("[n]", String(Math.max(10, (7 - this.step) * 10)));
    const prev = this.querySelector("[data-fq-prev]");
    if (prev) prev.hidden = result || this.step === 1;
    const cont = this.querySelector("[data-fq-continue]");
    if (cont) {
      cont.hidden = this.step !== 6;
      cont.disabled = !this.answers.cond.length;
    }
    const foot = this.querySelector("[data-fq-foot]");
    if (foot) foot.hidden = result || this.step === 1;
  }
  #result() {
    const t = this.t;
    const a = this.answers;
    const gender = a.shoeGrid === "men" ? "men" : "women";
    const shoe = Number(a.shoe);
    const rows = this.sizes.filter((row) => row[gender]);
    if (!rows.length) return;
    let index = rows.findIndex((row) => shoe >= row[gender].min && shoe <= row[gender].max);
    let edge = "";
    if (index < 0) {
      if (shoe > rows[rows.length - 1][gender].max) {
        index = rows.length - 1;
        edge = "above";
      } else if (shoe < rows[0][gender].min) {
        index = 0;
        edge = "below";
      } else index = rows.findIndex((row) => shoe < row[gender].min);
    }
    const base = rows[index];
    const span = base[gender].max - base[gender].min || 1;
    const position = Math.min(Math.max((shoe - base[gender].min) / span, 0), 1);
    const calf = { slim: -1, average: 0, full: 1, very: 2 }[a.calf] ?? 0;
    const swell = { no: 0, some: 0.5, lot: 1 }[a.swell] ?? 0;
    const feel = { firm: -1, balanced: 0, gentle: 1 }[a.feel] ?? 0;
    const hard = a.cond.includes("hard") ? 0.5 : 0;
    const lean = calf + swell + feel + hard;
    let final = index;
    let adjust = "";
    if (!edge && index < rows.length - 1 && (lean >= 2.5 || lean >= 1 && position >= 0.5)) {
      final = index + 1;
      adjust = position >= 0.5 ? "up" : "up_far";
    } else if (!edge && index > 0 && lean <= -1.5 && position <= 0.34 && shoe <= rows[index - 1][gender].max + 0.5) {
      final = index - 1;
      adjust = "down";
    }
    const chosen = rows[final];
    this.result = chosen.size;
    const who = gender === "men" ? t.men : t.women;
    const system = this.system.toUpperCase();
    const shoeText = this.#num(this.#convert(shoe, gender));
    const fill = (s, v) => String(s || "").replace(/\[(\w+)\]/g, (m, k) => k in v ? v[k] : m);
    const reasons = [];
    const shoeKey = edge === "above" ? "r_shoe_above" : edge === "below" ? "r_shoe_below" : "r_shoe";
    reasons.push([t.r_shoe_label, fill(t[shoeKey], { system, who, shoe: shoeText, size: base.size, range: this.#pretty(base[gender], gender) })]);
    if (a.calf) reasons.push([t.r_calf_label, t[`r_calf_${a.calf}`]]);
    if (a.swell) reasons.push([t.r_swell_label, t[`r_swell_${a.swell}`]]);
    if (a.feel) reasons.push([t.r_feel_label, t[`r_feel_${a.feel}`]]);
    if (hard) reasons.push([t.r_hard_label, t.r_hard]);
    if (a.wear) reasons.push([t.r_wear_label, t[`r_wear_${a.wear}`]]);
    if (adjust) reasons.push([t.r_adjust_label, fill(t[`r_adjust_${adjust}`], { base: base.size })]);
    const range = fill(gender === "men" ? t.range_men : t.range_women, { system, range: this.#pretty(chosen[gender], gender) });
    this.querySelector("[data-fq-size]").textContent = chosen.size;
    this.querySelector("[data-fq-range]").textContent = range;
    this.resultLabel = `${chosen.size} (${range})`;
    const sentence = (text) => {
      const s = String(text || "").trim();
      return s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}${/[.!?]$/.test(s) ? "" : "."}` : "";
    };
    const why = this.querySelector("[data-fq-why]");
    if (why) {
      const shoeLine = reasons[0]?.[1];
      const adjustLine = adjust ? reasons[reasons.length - 1][1] : "";
      why.textContent = [sentence(shoeLine), sentence(adjustLine)].filter(Boolean).join(" ");
    }
    const label = this.querySelector("[data-fq-select-label]");
    if (label) label.textContent = fill(t.select, { size: chosen.size });
  }
  /* Coche la tuile de taille (masquée dans le bundle) : la fiche applique la
     taille à toutes les paires. */
  #select() {
    if (!this.result) return;
    const scope = this.closest(".shopify-section") || document;
    const value = window.CSS && CSS.escape ? CSS.escape(this.result) : this.result;
    const input = scope.querySelector(`.nappi-product__tile input[value="${value}"]`);
    if (input) input.click();
    scope.querySelectorAll(`[data-modal-open="${this.id}"].nappi-bundle__finder`).forEach((button) => {
      button.textContent = (this.t.recommended || "").replace("[size]", this.resultLabel || this.result);
    });
    this.close();
  }
}
class NappiTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.select(tab.dataset.tab));
      tab.addEventListener("keydown", (event) => {
        const i = this.tabs.indexOf(tab);
        if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
          event.preventDefault();
          const next = this.tabs[(i + (event.key === "ArrowRight" ? 1 : -1) + this.tabs.length) % this.tabs.length];
          this.select(next.dataset.tab);
          next.focus();
        }
      });
    });
  }
  select(key) {
    this.tabs.forEach((tab) => {
      const on = tab.dataset.tab === key;
      tab.setAttribute("aria-selected", on ? "true" : "false");
      tab.tabIndex = on ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.tab !== key;
    });
    const indicator = this.querySelector("[data-tab-indicator]");
    const active = this.tabs.find((t) => t.dataset.tab === key);
    if (indicator && active) {
      indicator.style.width = `${Math.max(0, active.offsetWidth - 8)}px`;
      indicator.style.transform = `translateX(${active.offsetLeft - this.tabs[0].offsetLeft}px)`;
    }
  }
}
if (!customElements.get("nappi-product")) customElements.define("nappi-product", NappiProduct);
if (!customElements.get("nappi-gallery")) customElements.define("nappi-gallery", NappiGallery);
class NappiStickyBuy extends HTMLElement {
  connectedCallback() {
    this.bouton = this.querySelector("[data-sticky-add]");
    this.vrai = document.querySelector("[data-nappi-add]");
    if (!this.bouton || !this.vrai) return;
    this.bouton.addEventListener("click", () => this.vrai.click());
    this.observateur = new IntersectionObserver(
      ([entree]) => {
        const sortiParLeHaut = !entree.isIntersecting && entree.boundingClientRect.top < 0;
        this.#afficher(sortiParLeHaut);
      },
      { rootMargin: "0px" }
    );
    this.observateur.observe(this.vrai);
    this.#recopier();
    this.suivi = new MutationObserver(() => this.#recopier());
    this.suivi.observe(this.vrai, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["disabled"]
    });
    this.prix = this.querySelector("[data-sticky-price]");
    this.paliers = document.querySelector(".nappi-product__tiers");
    if (this.prix && this.paliers) {
      this.#majPrix();
      this.paliers.addEventListener("change", () => this.#majPrix());
      this.suiviPrix = new MutationObserver(() => this.#majPrix());
      this.suiviPrix.observe(this.paliers, { characterData: true, childList: true, subtree: true });
    }
  }
  disconnectedCallback() {
    this.observateur?.disconnect();
    this.suivi?.disconnect();
    this.suiviPrix?.disconnect();
    clearTimeout(this.minuteur);
    document.documentElement.style.removeProperty("--nappi-stickybar-space");
  }
  #majPrix() {
    const coche = this.paliers.querySelector("[data-tier-input]:checked");
    if (!coche && this.paliers.closest("[data-bundle]")) {
      const seul = this.vrai.querySelector("[data-bundle-total]")?.textContent.trim() || "";
      this.prix.textContent = "";
      const el = document.createElement("strong");
      el.className = "nappi-product__stickybar-now";
      el.textContent = seul;
      this.prix.appendChild(el);
      this.prix.hidden = !seul;
      return;
    }
    const palier = coche ? coche.closest("[data-tier]") : this.paliers.querySelector("[data-tier]");
    if (!palier) {
      this.prix.hidden = true;
      return;
    }
    const lire = (sel) => palier.querySelector(sel)?.textContent.trim() || "";
    const barre = lire("[data-tier-compare]");
    const total = lire("[data-tier-total]");
    const unite = lire(".nappi-product__tier-unit");
    if (!total) {
      this.prix.hidden = true;
      return;
    }
    this.prix.textContent = "";
    const poser = (balise, classe, texte) => {
      if (!texte) return;
      const el = document.createElement(balise);
      el.className = classe;
      el.textContent = texte;
      this.prix.appendChild(el);
    };
    poser("s", "nappi-product__stickybar-was", barre);
    poser("strong", "nappi-product__stickybar-now", total);
    poser("span", "nappi-product__stickybar-unit", unite);
    this.prix.hidden = false;
  }
  #afficher(oui) {
    if (oui) {
      clearTimeout(this.minuteur);
      this.hidden = false;
      document.documentElement.style.setProperty("--nappi-stickybar-space", `${this.offsetHeight}px`);
      requestAnimationFrame(() => this.classList.add("is-shown"));
      return;
    }
    this.classList.remove("is-shown");
    clearTimeout(this.minuteur);
    this.minuteur = setTimeout(() => {
      this.hidden = true;
    }, 260);
  }
  #recopier() {
    const etiquette = this.vrai.querySelector("[data-nappi-add-label]");
    if (etiquette) this.bouton.textContent = etiquette.textContent.trim();
    this.bouton.disabled = this.vrai.disabled;
  }
}
if (!customElements.get("nappi-sticky-buy")) customElements.define("nappi-sticky-buy", NappiStickyBuy);
if (!customElements.get("nappi-modal")) customElements.define("nappi-modal", NappiModal);
if (!customElements.get("nappi-size-guide")) customElements.define("nappi-size-guide", NappiSizeGuide);
if (!customElements.get("nappi-tabs")) customElements.define("nappi-tabs", NappiTabs);
if (!customElements.get("nappi-size-finder")) customElements.define("nappi-size-finder", NappiSizeFinder);
if (!customElements.get("nappi-fit-quiz")) customElements.define("nappi-fit-quiz", NappiFitQuiz);
